/**
 * Slice-2 identity badges for the pinned business-page header: Vendor kind, program affiliation (e.g. "Sheepdog:
 * Confirmed"), Sales Account Owner and Concierge rep. Shares React Query keys with the cards that edit them, so the
 * header updates as soon as a card saves.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getVendorProfile } from "../../services/organizationVendor.service";
import {
  getConciergeTeam,
  getOrganizationConcierge,
  getOrganizationSalesOwner,
} from "../../services/organizationPeople.service";
import {
  getProgramAffiliations,
  programLabel,
  statusLabel,
} from "../../services/organizationPrograms.service";
import { affiliationChipClass } from "./ProgramAffiliationPanel";

interface Props {
  organizationId: string;
  organizationType?: string | null;
  compact?: boolean;
}

const OrganizationHeaderBadges: React.FC<Props> = ({
  organizationId,
  organizationType,
  compact,
}) => {
  const { data: vendor } = useQuery({
    queryKey: ["vendor-profile", organizationId],
    queryFn: () => getVendorProfile(organizationId),
  });
  const { data: programs = [] } = useQuery({
    queryKey: ["program-affiliations", organizationId],
    queryFn: () => getProgramAffiliations(organizationId),
  });
  const { data: owner } = useQuery({
    queryKey: ["organization-sales-owner", organizationId],
    queryFn: () => getOrganizationSalesOwner(organizationId),
  });
  const { data: conciergeId } = useQuery({
    queryKey: ["organization-concierge", organizationId],
    queryFn: () => getOrganizationConcierge(organizationId),
  });
  const { data: team = [] } = useQuery({
    queryKey: ["concierge-team"],
    queryFn: getConciergeTeam,
    enabled: !!conciergeId,
  });
  const concierge = team.find((m) => m.profile_id === conciergeId);
  const vendorOnly = !(organizationType || "").trim();

  return (
    <>
      {vendor?.is_active && (
        <span
          className="rounded bg-purple-100 px-2 py-0.5 text-purple-800"
          data-testid="summary-vendor"
        >
          {vendorOnly ? "Vendor" : "Vendor too"}
        </span>
      )}
      {programs
        .filter((p) => p.status !== "ended")
        .map((p) => (
          <span
            key={p.id}
            className={`rounded px-2 py-0.5 ${affiliationChipClass(p.status)}`}
            data-testid="summary-program"
          >
            {programLabel(p.program)}: {statusLabel(p.status)}
          </span>
        ))}
      {!compact && (
        <>
          <span className="text-gray-600" data-testid="summary-owner">
            Owner:{" "}
            {owner ? (
              owner.assigned_name || owner.title
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
          <span className="text-gray-600" data-testid="summary-concierge">
            Concierge:{" "}
            {conciergeId ? (
              concierge?.full_name || concierge?.email || "assigned"
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </>
      )}
    </>
  );
};

export default OrganizationHeaderBadges;
